import type { Observable } from 'rxjs';
import { map, timeout } from 'rxjs/operators';
import type { LifeCycleProductsPagination } from 'state/discover/page/page.model';
import type { IHttpClient } from 'utils/http/httpClient.model';

export function lifeCycleApiConnector(httpClient: IHttpClient, timeoutInMillis = 60000) {
  return {
    fetchNearToMaturity(
      pageNumber: number,
      pageSize: number,
    ): Observable<LifeCyclePaginatedProducts<ProductNearToMaturity>> {
      return httpClient
        .getJSON<LifeCyclePaginatedProducts<ProductNearToMaturity>>({
          url: `api/v1/lifecycle/products/closed-to-maturity`,
          params: { pageNumber, pageSize },
          targetHost: 'AdvisoryBridge',
        })
        .pipe(
          timeout(timeoutInMillis),
          map(r => r.payload),
        );
    },
    fetchWithNoMoreOptionValue(
      pageNumber: number,
      pageSize: number,
    ): Observable<LifeCyclePaginatedProducts<ProductWithNoMoreOptionValue>> {
      return httpClient
        .getJSON<LifeCyclePaginatedProducts<ProductWithNoMoreOptionValue>>({
          url: `api/v1/lifecycle/products/with-no-more-value-option`,
          params: { pageNumber, pageSize },
          targetHost: 'AdvisoryBridge',
        })
        .pipe(
          timeout(timeoutInMillis),
          map(r => r.payload),
        );
    },
    fetchNearToCapitalBarrier(
      pageNumber: number,
      pageSize: number,
    ): Observable<LifeCyclePaginatedProducts<ProductNearToLimit>> {
      return httpClient
        .getJSON<LifeCyclePaginatedProducts<ProductNearToLimit>>({
          url: `api/v1/lifecycle/products/capital-barrier-less-than`,
          params: { pageNumber, pageSize },
          targetHost: 'AdvisoryBridge',
        })
        .pipe(
          timeout(timeoutInMillis),
          map(r => r.payload),
        );
    },
    fetchNearToAutocall(
      pageNumber: number,
      pageSize: number,
    ): Observable<LifeCyclePaginatedProducts<ProductNearToLimit>> {
      return httpClient
        .getJSON<LifeCyclePaginatedProducts<ProductNearToLimit>>({
          url: `api/v1/lifecycle/products/close-to-autocall`,
          params: { pageNumber, pageSize },
          targetHost: 'AdvisoryBridge',
        })
        .pipe(
          timeout(timeoutInMillis),
          map(r => r.payload),
        );
    },
    fetchWithCapitalBarrierEvent(
      pageNumber: number,
      pageSize: number,
    ): Observable<LifeCyclePaginatedProducts<ProductNearToLimit>> {
      return httpClient
        .getJSON<LifeCyclePaginatedProducts<ProductNearToLimit>>({
          url: `api/v1/lifecycle/products/with-capital-barrier-event`,
          params: { pageNumber, pageSize },
          targetHost: 'AdvisoryBridge',
        })
        .pipe(
          timeout(timeoutInMillis),
          map(r => r.payload),
        );
    },
  };
}

export interface LifeCyclePaginatedProducts<T extends LifeCycleProduct> {
  data: T[];
  pagination: LifeCycleProductsPagination;
}

export interface LifeCycleProduct {
  productKey: string;
  productCategory: string;
  productName: string;
  isin: string;
}

export interface ProductNearToMaturity extends LifeCycleProduct {
  daysToMaturity: number;
}

export interface ProductWithNoMoreOptionValue extends LifeCycleProduct {
  vega: number;
  gamma: number;
}

export interface ProductNearToLimit extends LifeCycleProduct {
  distanceToCapitalLimit?: Nullable<number>;
  distanceToEarlyRedemptionLimit?: Nullable<number>;
  nextCapitalObservationDate?: Nullable<string>;
  underlyingList: ReadonlyArray<{
    bloombergCode: string;
    distanceToCapitalLimit?: Nullable<number>;
    distanceToEarlyRedemptionLimit?: Nullable<number>;
    nextCapitalObservationDate?: Nullable<string>;
    earlyRedemptionLimitHit?: Nullable<boolean>;
    capitalLimitHit?: Nullable<boolean>;
  }>;
}
